import { CanvasLayerCache } from "./canvasLayerCache";

type RandomTeamsCanvasMode = "teams" | "orders" | null;

type RandomTeamsCanvasPoint = {
    x: number;
    y: number;
};

export type RandomTeamsCanvasClipRect = {
    x: number;
    y: number;
    width: number;
    height: number;
    radius: number;
};

export type RandomTeamsCanvasConnectorGlow = {
    clipRect: RandomTeamsCanvasClipRect;
    start: RandomTeamsCanvasPoint;
    exitControlX: number;
    rightEdgeX: number;
    rightTurnX: number;
    midY: number;
    leftTurnX: number;
    leftEdgeX: number;
    enterControlX: number;
    end: RandomTeamsCanvasPoint;
};

export type RandomTeamsCanvasImpactBurst = {
    clipRect: RandomTeamsCanvasClipRect;
    x: number;
    y: number;
    tiltDeg: number;
    solid: string;
    paint: string;
    soft: string;
    glow: string;
};

type RandomTeamsCanvasState = {
    hasPreview: boolean;
    hasResult: boolean;
    launchInProgress: boolean;
    motionEnabled: boolean;
    presentationOpen: boolean;
    resultMode: RandomTeamsCanvasMode;
};

export type RandomTeamsCanvasFramePerfSample = {
    reason: string;
    recordedAt: number;
    totalMs: number;
    canvasCssWidth: number;
    canvasCssHeight: number;
    devicePixelRatio: number;
    connectorGlowCount: number;
    impactBurstCount: number;
    phases: Record<string, number>;
};

type RandomTeamsCanvasRendererOptions = {
    onPerfSample?: ((sample: RandomTeamsCanvasFramePerfSample) => void) | null;
};

const MAX_DEVICE_PIXEL_RATIO = 2;
const AMBIENT_FRAME_INTERVAL_MS = 1000 / 30;
const DEFAULT_STATE: RandomTeamsCanvasState = {
    hasPreview: false,
    hasResult: false,
    launchInProgress: false,
    motionEnabled: true,
    presentationOpen: false,
    resultMode: null
};

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

const mix = (from: number, to: number, amount: number): number => from + (to - from) * amount;

const easeInOut = (value: number): number => 0.5 - Math.cos(clamp(value, 0, 1) * Math.PI) / 2;

const unitFromIndex = (index: number, offset = 0): number => {
    const seed = (index * 73 + offset * 131 + 17) % 997;
    return seed / 997;
};

const colorWithAlpha = (red: number, green: number, blue: number, alpha: number): string =>
    `rgba(${red}, ${green}, ${blue}, ${alpha})`;

const getPointAlongPolyline = (points: RandomTeamsCanvasPoint[], progress: number): RandomTeamsCanvasPoint => {
    if (points.length === 0) {
        return { x: 0, y: 0 };
    }

    if (points.length === 1) {
        return points[0];
    }

    const segmentLengths = points.slice(1).map((point, index) => {
        const previous = points[index];
        return Math.hypot(point.x - previous.x, point.y - previous.y);
    });
    const totalLength = segmentLengths.reduce((total, length) => total + length, 0);
    const targetLength = totalLength * clamp(progress, 0, 1);
    let traversed = 0;

    for (let index = 0; index < segmentLengths.length; index += 1) {
        const segmentLength = segmentLengths[index];
        const start = points[index];
        const end = points[index + 1];

        if (targetLength <= traversed + segmentLength || index === segmentLengths.length - 1) {
            const amount = segmentLength <= 0 ? 0 : (targetLength - traversed) / segmentLength;
            return {
                x: mix(start.x, end.x, amount),
                y: mix(start.y, end.y, amount)
            };
        }

        traversed += segmentLength;
    }

    return points[points.length - 1];
};

const traceRoundedRect = (
    context: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
) => {
    const nextRadius = clamp(radius, 0, Math.min(width, height) / 2);

    context.moveTo(x + nextRadius, y);
    context.arcTo(x + width, y, x + width, y + height, nextRadius);
    context.arcTo(x + width, y + height, x, y + height, nextRadius);
    context.arcTo(x, y + height, x, y, nextRadius);
    context.arcTo(x, y, x + width, y, nextRadius);
    context.closePath();
};

export class RandomTeamsCanvasRenderer {
    private readonly context: CanvasRenderingContext2D | null;
    private readonly onPerfSample: ((sample: RandomTeamsCanvasFramePerfSample) => void) | null;
    readonly isAvailable: boolean;
    private readonly backdropCache = new CanvasLayerCache();
    private readonly edgeCache = new CanvasLayerCache();
    private readonly tileGlowSprites = new Map<string, HTMLCanvasElement>();
    private cssWidth = 0;
    private cssHeight = 0;
    private frameId: number | null = null;
    private frameTimerId: number | null = null;
    private isActive = true;
    private state: RandomTeamsCanvasState = DEFAULT_STATE;
    private orderConnectorGlows: RandomTeamsCanvasConnectorGlow[] = [];
    private impactBursts: Array<RandomTeamsCanvasImpactBurst & { createdAt: number; duration: number }> = [];

    constructor(private readonly canvas: HTMLCanvasElement, options: RandomTeamsCanvasRendererOptions = {}) {
        this.context = canvas.getContext("2d");
        this.onPerfSample = options.onPerfSample ?? null;
        this.isAvailable = this.context !== null;
    }

    destroy() {
        this.backdropCache.destroy();
        this.edgeCache.destroy();
        this.tileGlowSprites.forEach((sprite) => {
            sprite.width = sprite.height = 1;
        });
        this.tileGlowSprites.clear();
        this.cancelScheduledFrame();

        this.orderConnectorGlows = [];
        this.impactBursts = [];
        this.clear();
    }

    setViewport(width: number, height: number) {
        const nextWidth = Math.max(1, Math.round(width));
        const nextHeight = Math.max(1, Math.round(height));

        if (this.cssWidth === nextWidth && this.cssHeight === nextHeight
            && this.canvas.width === Math.round(nextWidth * this.getDevicePixelRatio())
            && this.canvas.height === Math.round(nextHeight * this.getDevicePixelRatio())) {
            return;
        }

        this.cssWidth = nextWidth;
        this.cssHeight = nextHeight;

        const pixelRatio = this.getDevicePixelRatio();
        const pixelWidth = Math.max(1, Math.round(nextWidth * pixelRatio));
        const pixelHeight = Math.max(1, Math.round(nextHeight * pixelRatio));

        if (this.canvas.width !== pixelWidth) {
            this.canvas.width = pixelWidth;
        }

        if (this.canvas.height !== pixelHeight) {
            this.canvas.height = pixelHeight;
        }

        this.renderNow("viewport");
    }

    setState(nextState: RandomTeamsCanvasState) {
        const didStateChange = (Object.keys(nextState) as Array<keyof RandomTeamsCanvasState>).some(
            (key) => nextState[key] !== this.state[key]
        );
        const launchChanged = nextState.launchInProgress !== this.state.launchInProgress;
        this.state = nextState;
        if (launchChanged) {
            this.cancelScheduledFrame();
        }
        this.syncLoop();

        if (didStateChange) {
            this.renderNow("state");
        }
    }

    setActive(isActive: boolean) {
        if (this.isActive === isActive) {
            return;
        }

        this.isActive = isActive;

        if (!isActive) {
            this.cancelScheduledFrame();
            return;
        }

        this.renderNow("active");
        this.syncLoop();
    }

    setOrderConnectorGlows(nextConnectorGlows: RandomTeamsCanvasConnectorGlow[]) {
        this.orderConnectorGlows = nextConnectorGlows;
        this.renderNow("connectors");
    }

    spawnImpactBurst(burst: RandomTeamsCanvasImpactBurst) {
        if (!this.isAvailable || !this.state.motionEnabled) {
            return;
        }

        this.impactBursts.push({
            ...burst,
            createdAt: performance.now(),
            duration: 760
        });
        this.cancelScheduledFrame();
        this.syncLoop();
        this.renderNow("impact");
    }

    clearTransientEffects() {
        if (this.impactBursts.length === 0) {
            return;
        }

        this.impactBursts = [];
        this.renderNow("clear-effects");
    }

    private syncLoop() {
        if (!this.isAvailable) {
            return;
        }

        // The compositor owns student flights. Keep their backdrop still during
        // the launch so decorative canvas paint cannot compete with the reveal.
        if (!this.isActive || !this.state.motionEnabled || this.state.launchInProgress) {
            this.cancelScheduledFrame();
            return;
        }

        if (this.frameId !== null || this.frameTimerId !== null) {
            return;
        }

        if (this.impactBursts.length > 0) {
            this.frameId = window.requestAnimationFrame(this.tick);
            return;
        }

        this.frameTimerId = window.setTimeout(() => {
            this.frameTimerId = null;

            if (this.isActive && this.state.motionEnabled && this.frameId === null) {
                this.frameId = window.requestAnimationFrame(this.tick);
            }
        }, AMBIENT_FRAME_INTERVAL_MS);
    }

    private readonly tick = (recordedAt: number) => {
        this.frameId = null;
        this.render(recordedAt, "tick");
        this.syncLoop();
    };

    private cancelScheduledFrame() {
        if (this.frameId !== null) {
            window.cancelAnimationFrame(this.frameId);
            this.frameId = null;
        }

        if (this.frameTimerId !== null) {
            window.clearTimeout(this.frameTimerId);
            this.frameTimerId = null;
        }
    }

    private renderNow(reason: string) {
        if (!this.isActive) {
            return;
        }

        this.render(this.state.motionEnabled ? performance.now() : 0, reason);
    }

    private clear() {
        if (!this.context) {
            return;
        }

        this.context.setTransform(1, 0, 0, 1, 0, 0);
        this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }

    private render(recordedAt: number, reason: string) {
        const context = this.context;

        if (!context || this.cssWidth <= 0 || this.cssHeight <= 0) {
            return;
        }

        const shouldMeasurePerf = this.onPerfSample !== null;
        const renderStartedAt = shouldMeasurePerf ? performance.now() : 0;
        const phases: Record<string, number> = {};

        const pruneStartedAt = shouldMeasurePerf ? performance.now() : 0;
        this.pruneImpactBursts(recordedAt);
        if (shouldMeasurePerf) {
            phases.prune = performance.now() - pruneStartedAt;
        }

        const clearStartedAt = shouldMeasurePerf ? performance.now() : 0;
        this.clear();
        if (shouldMeasurePerf) {
            phases.clear = performance.now() - clearStartedAt;
        }
        context.setTransform(this.getDevicePixelRatio(), 0, 0, this.getDevicePixelRatio(), 0, 0);
        context.globalCompositeOperation = "source-over";
        context.imageSmoothingEnabled = true;

        const width = this.cssWidth;
        const height = this.cssHeight;
        const time = this.state.motionEnabled ? recordedAt : 0;
        const energy = this.getEnergy();

        const backdropStartedAt = shouldMeasurePerf ? performance.now() : 0;
        this.drawBackdropSweep(context, width, height, time, energy);
        if (shouldMeasurePerf) {
            phases.backdrop = performance.now() - backdropStartedAt;
        }

        const modeStartedAt = shouldMeasurePerf ? performance.now() : 0;
        if (this.state.hasPreview) {
            this.drawPreviewGeometry(context, width, height, time, energy);
        } else if (this.state.resultMode === "orders") {
            this.drawOrderCurrents(context, width, height, time, energy);
        } else {
            this.drawTeamCurrents(context, width, height, time, energy);
        }
        if (shouldMeasurePerf) {
            phases.mode = performance.now() - modeStartedAt;
        }

        if (this.orderConnectorGlows.length > 0) {
            const connectorsStartedAt = shouldMeasurePerf ? performance.now() : 0;
            this.drawOrderConnectorGlows(context, time, energy);
            if (shouldMeasurePerf) {
                phases.connectors = performance.now() - connectorsStartedAt;
            }
        }

        const motesStartedAt = shouldMeasurePerf ? performance.now() : 0;
        this.drawGeometricMotes(context, width, height, time, energy);
        if (shouldMeasurePerf) {
            phases.motes = performance.now() - motesStartedAt;
        }

        if (this.impactBursts.length > 0) {
            const burstsStartedAt = shouldMeasurePerf ? performance.now() : 0;
            this.drawImpactBursts(context, time, energy);
            if (shouldMeasurePerf) {
                phases.bursts = performance.now() - burstsStartedAt;
            }
        }

        const edgeStartedAt = shouldMeasurePerf ? performance.now() : 0;
        this.edgeCache.draw(context, width, height, this.getDevicePixelRatio(), "edge",
            (layer) => this.drawEdgeShade(layer, width, height));
        if (shouldMeasurePerf) {
            phases.edge = performance.now() - edgeStartedAt;
            this.onPerfSample?.({
                reason,
                recordedAt: performance.now(),
                totalMs: performance.now() - renderStartedAt,
                canvasCssWidth: width,
                canvasCssHeight: height,
                devicePixelRatio: this.getDevicePixelRatio(),
                connectorGlowCount: this.orderConnectorGlows.length,
                impactBurstCount: this.impactBursts.length,
                phases
            });
        }
    }

    private pruneImpactBursts(recordedAt: number) {
        if (this.impactBursts.length === 0) {
            return;
        }

        const now = recordedAt > 0 ? recordedAt : performance.now();
        this.impactBursts = this.impactBursts.filter((burst) => now - burst.createdAt < burst.duration);
    }

    private getEnergy(): number {
        const presentationBoost = this.state.presentationOpen ? 1.14 : 1;
        const launchBoost = this.state.launchInProgress ? 1.22 : 1;
        const previewSoftener = this.state.hasPreview && !this.state.hasResult ? 0.86 : 1;
        return presentationBoost * launchBoost * previewSoftener;
    }

    private getDevicePixelRatio(): number {
        if (typeof window === "undefined") {
            return 1;
        }

        return clamp(window.devicePixelRatio || 1, 1, MAX_DEVICE_PIXEL_RATIO);
    }

    private drawBackdropSweep(
        context: CanvasRenderingContext2D,
        width: number,
        height: number,
        time: number,
        energy: number
    ) {
        this.backdropCache.draw(context, width, height, this.getDevicePixelRatio(), String(energy), (context) => {
            const cornerGlow = context.createRadialGradient(
                width * 0.88,
                height * 0.76,
                0,
                width * 0.88,
                height * 0.76,
                Math.max(width, height) * 0.52
            );
            cornerGlow.addColorStop(0, colorWithAlpha(31, 225, 209, 0.11 * energy));
            cornerGlow.addColorStop(0.45, colorWithAlpha(31, 225, 209, 0.038 * energy));
            cornerGlow.addColorStop(1, colorWithAlpha(31, 225, 209, 0));
            context.fillStyle = cornerGlow;
            context.fillRect(0, 0, width, height);

            const upperMist = context.createLinearGradient(0, 0, width, height * 0.46);
            upperMist.addColorStop(0, colorWithAlpha(104, 214, 255, 0.032 * energy));
            upperMist.addColorStop(0.48, colorWithAlpha(255, 240, 186, 0.016 * energy));
            upperMist.addColorStop(1, colorWithAlpha(255, 255, 255, 0));
            context.fillStyle = upperMist;
            context.fillRect(0, 0, width, height * 0.54);

        });

        const sweepTravel = width + height * 0.8;
        const leadingX = ((time * 0.024 * energy) % sweepTravel) - height * 0.42;
        const sweep = context.createLinearGradient(leadingX, 0, leadingX + height * 0.55, 0);
        sweep.addColorStop(0, colorWithAlpha(255, 255, 255, 0));
        sweep.addColorStop(0.45, colorWithAlpha(180, 255, 246, 0.024 * energy));
        sweep.addColorStop(0.55, colorWithAlpha(255, 249, 212, 0.03 * energy));
        sweep.addColorStop(1, colorWithAlpha(255, 255, 255, 0));
        context.fillStyle = sweep;
        context.fillRect(0, 0, width, height);

        const lowerSweepX = width - (((time * 0.018 * energy) % (width + height * 0.45)) - height * 0.12);
        const lowerSweep = context.createLinearGradient(lowerSweepX, height, lowerSweepX - height * 0.38, 0);
        lowerSweep.addColorStop(0, colorWithAlpha(255, 255, 255, 0));
        lowerSweep.addColorStop(0.5, colorWithAlpha(86, 204, 255, 0.036 * energy));
        lowerSweep.addColorStop(1, colorWithAlpha(255, 255, 255, 0));
        context.fillStyle = lowerSweep;
        context.fillRect(0, 0, width, height);
    }

    private drawPreviewGeometry(
        context: CanvasRenderingContext2D,
        width: number,
        height: number,
        time: number,
        energy: number
    ) {
        const pulse = 0.68 + 0.32 * Math.sin(time * 0.0031);
        const leftAnchorX = width * (this.state.presentationOpen ? 0.32 : 0.28) + Math.sin(time * 0.00042) * width * 0.014;
        const leftAnchorY = height * 0.56 + Math.cos(time * 0.00054) * height * 0.018;
        const rightAnchorX = width * (this.state.presentationOpen ? 0.7 : 0.74) + Math.cos(time * 0.00034) * width * 0.012;
        const rightAnchorY = height * 0.42 + Math.sin(time * 0.00048 + 1.4) * height * 0.02;
        const lowerAnchorX = width * 0.5 + Math.sin(time * 0.00032 + 2.2) * width * 0.012;
        const lowerAnchorY = height * 0.72 + Math.cos(time * 0.00038 + 0.6) * height * 0.014;
        const clusterSpread = Math.min(width, height) * (this.state.presentationOpen ? 0.18 : 0.15);

        // Preview stays mode-agnostic so toggling Teams/Orders does not jolt the idle ambience.
        this.drawTileCluster(context, leftAnchorX, leftAnchorY, clusterSpread, [86, 246, 227], time, energy, 11, 7);
        this.drawTileCluster(context, rightAnchorX, rightAnchorY, clusterSpread * 0.86, [255, 227, 156], time, energy, 37, 6);
        this.drawTileCluster(context, lowerAnchorX, lowerAnchorY, clusterSpread * 0.66, [118, 226, 255], time, energy, 61, 5);

        const guideLines = [
            {
                points: [
                    { x: leftAnchorX + clusterSpread * 0.42, y: leftAnchorY - clusterSpread * 0.36 },
                    { x: width * 0.42, y: leftAnchorY - clusterSpread * 0.36 },
                    { x: width * 0.42, y: rightAnchorY - clusterSpread * 0.34 },
                    { x: rightAnchorX - clusterSpread * 0.34, y: rightAnchorY - clusterSpread * 0.34 }
                ],
                color: [173, 255, 248] as const,
                alpha: 0.12,
                width: 1.55
            },
            {
                points: [
                    { x: leftAnchorX + clusterSpread * 0.18, y: leftAnchorY + clusterSpread * 0.18 },
                    { x: width * 0.54, y: leftAnchorY + clusterSpread * 0.18 },
                    { x: width * 0.54, y: rightAnchorY + clusterSpread * 0.22 },
                    { x: rightAnchorX - clusterSpread * 0.1, y: rightAnchorY + clusterSpread * 0.22 }
                ],
                color: [255, 236, 182] as const,
                alpha: 0.1,
                width: 1.35
            },
            {
                points: [
                    { x: lowerAnchorX - clusterSpread * 0.52, y: lowerAnchorY - clusterSpread * 0.06 },
                    { x: width * 0.34, y: lowerAnchorY - clusterSpread * 0.06 },
                    { x: width * 0.34, y: leftAnchorY + clusterSpread * 0.42 },
                    { x: leftAnchorX - clusterSpread * 0.26, y: leftAnchorY + clusterSpread * 0.42 }
                ],
                color: [118, 226, 255] as const,
                alpha: 0.09,
                width: 1.2
            }
        ];

        guideLines.forEach((guide, index) => {
            const drift = Math.sin(time * 0.001 + index * 1.7) * 4;
            const points = guide.points.map((point, pointIndex) => ({
                x: point.x,
                y: point.y + (pointIndex % 2 === 0 ? drift : -drift * 0.45)
            }));
            this.drawAngularGuidePath(context, points, guide.color, guide.alpha * pulse, guide.width, energy);

            const travel = (time * (0.00007 + index * 0.000012) + index * 0.24) % 1;
            const node = getPointAlongPolyline(points, travel);
            this.drawSoftTile(context, node.x, node.y, mix(6.5, 10.5, pulse), Math.PI * 0.25 + index * 0.18, guide.color, 0.2, energy);
        });
    }

    private drawAngularGuidePath(
        context: CanvasRenderingContext2D,
        points: RandomTeamsCanvasPoint[],
        color: readonly [number, number, number],
        alpha: number,
        lineWidth: number,
        energy: number
    ) {
        if (points.length < 2) {
            return;
        }

        const first = points[0];
        const last = points[points.length - 1];
        const gradient = context.createLinearGradient(first.x, first.y, last.x, last.y);
        gradient.addColorStop(0, colorWithAlpha(255, 255, 255, 0));
        gradient.addColorStop(0.2, colorWithAlpha(color[0], color[1], color[2], alpha * energy));
        gradient.addColorStop(0.78, colorWithAlpha(color[0], color[1], color[2], alpha * 0.72 * energy));
        gradient.addColorStop(1, colorWithAlpha(255, 255, 255, 0));

        context.save();
        context.strokeStyle = gradient;
        context.lineWidth = lineWidth;
        context.lineCap = "square";
        context.lineJoin = "miter";
        context.beginPath();
        context.moveTo(first.x, first.y);
        points.slice(1).forEach((point) => {
            context.lineTo(point.x, point.y);
        });
        context.stroke();
        context.restore();
    }

    private drawSoftTile(
        context: CanvasRenderingContext2D,
        x: number,
        y: number,
        size: number,
        rotation: number,
        color: readonly [number, number, number],
        alpha: number,
        energy: number,
        radiusScale = 0.18
    ) {
        const effectiveAlpha = alpha * energy;

        if (effectiveAlpha <= 0) {
            return;
        }

        const radius = size * radiusScale;

        context.save();
        context.translate(x, y);
        context.rotate(rotation);

        // Rasterize each palette's blur once into a small sprite. Live frames only
        // translate/scale its bitmap instead of blurring every ambient tile.
        const spriteKey = `${color.join(":")}:${radiusScale}`;
        let sprite = this.tileGlowSprites.get(spriteKey);
        if (!sprite && this.tileGlowSprites.size < 16) {
            sprite = document.createElement("canvas");
            sprite.width = sprite.height = 128;
            const layer = sprite.getContext("2d");
            if (layer) {
                layer.shadowBlur = 22.4;
                layer.shadowColor = colorWithAlpha(color[0], color[1], color[2], 0.46);
                layer.fillStyle = colorWithAlpha(color[0], color[1], color[2], 0.48);
                layer.beginPath();
                traceRoundedRect(layer, 48, 48, 32, 32, 32 * radiusScale);
                layer.fill();
                this.tileGlowSprites.set(spriteKey, sprite);
            } else {
                sprite = undefined;
            }
        }
        if (sprite) {
            context.globalAlpha = effectiveAlpha;
            context.drawImage(sprite, -size * 2, -size * 2, size * 4, size * 4);
            context.globalAlpha = 1;
        } else {
            context.fillStyle = colorWithAlpha(color[0], color[1], color[2], effectiveAlpha * 0.48);
            context.beginPath();
            traceRoundedRect(context, -size / 2, -size / 2, size, size, radius);
            context.fill();
        }

        context.strokeStyle = colorWithAlpha(255, 255, 255, effectiveAlpha * 0.26);
        context.lineWidth = clamp(size * 0.08, 0.7, 1.4);
        context.beginPath();
        traceRoundedRect(context, -size / 2, -size / 2, size, size, radius);
        context.stroke();
        context.restore();
    }

    private buildTileClusterLayout(spread: number, seed: number, count: number, time: number) {
        const leadingRowCount = Math.ceil(count / 2);
        const trailingRowCount = count - leadingRowCount;
        const rowCounts =
            trailingRowCount > 0 && seed % 4 === 0
                ? [trailingRowCount, leadingRowCount]
                : [leadingRowCount, trailingRowCount];
        const rowSpacing = spread * 0.22;
        const columnSpacing = spread * 0.18;
        const rowTilt = spread * 0.018 * (seed % 2 === 0 ? 1 : -1);
        const sharedDriftX = Math.sin(time * 0.00018 + seed * 0.9) * spread * 0.024;
        const sharedDriftY = Math.cos(time * 0.0002 + seed * 0.55) * spread * 0.018;
        const baseRotation = (unitFromIndex(0, seed + 83) - 0.5) * 0.14;
        const tiles: Array<{ x: number; y: number; size: number; rotation: number; alpha: number }> = [];
        let tileIndex = 0;

        rowCounts.forEach((rowCount, rowIndex) => {
            if (rowCount <= 0) {
                return;
            }

            const rowCenterY = (rowIndex - (rowCounts.length - 1) / 2) * rowSpacing;
            const staggerOffsetX = rowCount > 1 ? (rowIndex % 2 === 0 ? -columnSpacing * 0.28 : columnSpacing * 0.28) : 0;

            for (let index = 0; index < rowCount && tileIndex < count; index += 1) {
                const centeredColumn = index - (rowCount - 1) / 2;

                tiles.push({
                    x: sharedDriftX + staggerOffsetX + centeredColumn * columnSpacing,
                    y: sharedDriftY + rowCenterY + centeredColumn * rowTilt,
                    size: mix(spread * 0.075, spread * 0.118, unitFromIndex(tileIndex, seed + 41)),
                    rotation: baseRotation + centeredColumn * 0.018 + (rowIndex === 0 ? -0.06 : 0.06),
                    alpha: mix(0.09, 0.15, unitFromIndex(tileIndex, seed + 53))
                });
                tileIndex += 1;
            }
        });

        return tiles;
    }

    private drawTileCluster(
        context: CanvasRenderingContext2D,
        centerX: number,
        centerY: number,
        spread: number,
        color: readonly [number, number, number],
        time: number,
        energy: number,
        seed: number,
        count: number
    ) {
        const hazeWidth = spread * 1.65;
        const hazeHeight = spread * 1.18;

        context.save();
        context.translate(centerX, centerY);
        context.rotate(Math.sin(time * 0.00018 + seed) * 0.08);
        context.fillStyle = colorWithAlpha(color[0], color[1], color[2], 0.018 * energy);
        context.strokeStyle = colorWithAlpha(color[0], color[1], color[2], 0.035 * energy);
        context.lineWidth = 1;
        context.beginPath();
        traceRoundedRect(context, -hazeWidth / 2, -hazeHeight / 2, hazeWidth, hazeHeight, spread * 0.14);
        context.fill();
        context.stroke();
        context.restore();

        // Keep these ambient squares reading like interface pixels instead of drifting biological chains.
        this.buildTileClusterLayout(spread, seed, count, time).forEach((tile) => {
            this.drawSoftTile(context, centerX + tile.x, centerY + tile.y, tile.size, tile.rotation, color, tile.alpha, energy);
        });
    }

    private drawOrderCurrents(
        context: CanvasRenderingContext2D,
        width: number,
        height: number,
        time: number,
        energy: number
    ) {
        const laneWidth = clamp(width * (this.state.presentationOpen ? 0.16 : 0.19), 92, 188);
        const laneGradient = context.createLinearGradient(0, 0, laneWidth, 0);
        laneGradient.addColorStop(0, colorWithAlpha(255, 228, 132, 0.1 * energy));
        laneGradient.addColorStop(0.58, colorWithAlpha(255, 228, 132, 0.035 * energy));
        laneGradient.addColorStop(1, colorWithAlpha(255, 228, 132, 0));
        context.fillStyle = laneGradient;
        context.fillRect(0, height * 0.08, laneWidth, height * 0.84);

        for (let index = 0; index < 5; index += 1) {
            const rowRatio = 0.15 + index * 0.15;
            const startX = laneWidth * (0.82 + unitFromIndex(index, 4) * 0.1);
            const endX = width * (0.84 + unitFromIndex(index, 8) * 0.08);
            const startY = height * rowRatio + Math.sin(time * (0.00038 + index * 0.00006) + index * 1.7) * height * 0.012;
            const stepY =
                startY +
                (index % 2 === 0 ? 1 : -1) *
                    mix(height * 0.035, height * 0.068, unitFromIndex(index, 12));
            const endY = startY + Math.sin(time * 0.00042 + index * 1.1) * height * 0.018;
            const firstTurnX = mix(startX, endX, 0.28 + unitFromIndex(index, 16) * 0.08);
            const secondTurnX = mix(startX, endX, 0.64 + unitFromIndex(index, 20) * 0.1);
            const points = [
                { x: startX, y: startY },
                { x: firstTurnX, y: startY },
                { x: firstTurnX, y: stepY },
                { x: secondTurnX, y: stepY },
                { x: secondTurnX, y: endY },
                { x: endX, y: endY }
            ];
            const color = index % 2 === 0 ? ([255, 232, 158] as const) : ([160, 242, 255] as const);

            this.drawAngularGuidePath(context, points, color, 0.14, mix(1.1, 2.4, unitFromIndex(index, 24)), energy);

            const travel = (time * (0.00008 + index * 0.00001) + index * 0.18) % 1;
            const marker = getPointAlongPolyline(points, travel);
            this.drawSoftTile(context, marker.x, marker.y, mix(6, 11, unitFromIndex(index, 28)), Math.PI * 0.25, color, 0.2, energy);
        }
    }

    private drawOrderConnectorGlows(context: CanvasRenderingContext2D, time: number, energy: number) {
        this.orderConnectorGlows.forEach((connector, index) => {
            const pulse = 0.76 + 0.24 * Math.sin(time * 0.0022 + index * 1.34);
            const gradient = context.createLinearGradient(connector.start.x, connector.start.y, connector.end.x, connector.end.y);
            gradient.addColorStop(0, colorWithAlpha(255, 210, 102, 0));
            gradient.addColorStop(0.16, colorWithAlpha(255, 210, 102, 0.18 * pulse * energy));
            gradient.addColorStop(0.48, colorWithAlpha(255, 230, 168, 0.12 * pulse * energy));
            gradient.addColorStop(0.82, colorWithAlpha(164, 238, 255, 0.09 * pulse * energy));
            gradient.addColorStop(1, colorWithAlpha(164, 238, 255, 0));

            context.save();
            context.beginPath();
            traceRoundedRect(
                context,
                connector.clipRect.x,
                connector.clipRect.y,
                connector.clipRect.width,
                connector.clipRect.height,
                connector.clipRect.radius
            );
            context.clip();

            context.strokeStyle = gradient;
            context.lineCap = "round";
            context.lineJoin = "round";
            context.shadowBlur = 18;
            context.shadowColor = colorWithAlpha(255, 208, 104, 0.2 * pulse * energy);
            context.lineWidth = 10.5;
            context.beginPath();
            this.traceConnectorPath(context, connector);
            context.stroke();

            context.shadowBlur = 0;
            context.strokeStyle = colorWithAlpha(255, 238, 184, 0.12 * pulse * energy);
            context.lineWidth = 4.2;
            context.beginPath();
            this.traceConnectorPath(context, connector);
            context.stroke();

            this.fillConnectorGlowNode(context, connector.start.x, connector.start.y, 18, 0.22 * pulse * energy);
            this.fillConnectorGlowNode(context, connector.end.x, connector.end.y, 18, 0.2 * pulse * energy);
            context.restore();
        });
    }

    private traceConnectorPath(context: CanvasRenderingContext2D, connector: RandomTeamsCanvasConnectorGlow) {
        context.moveTo(connector.start.x, connector.start.y);
        context.bezierCurveTo(
            connector.exitControlX,
            connector.start.y,
            connector.rightEdgeX,
            connector.midY,
            connector.rightTurnX,
            connector.midY
        );
        context.lineTo(connector.leftTurnX, connector.midY);
        context.bezierCurveTo(
            connector.leftEdgeX,
            connector.midY,
            connector.enterControlX,
            connector.end.y,
            connector.end.x,
            connector.end.y
        );
    }

    private fillConnectorGlowNode(
        context: CanvasRenderingContext2D,
        x: number,
        y: number,
        size: number,
        alpha: number
    ) {
        context.save();
        context.translate(x, y);
        context.rotate(Math.PI * 0.25);
        context.shadowBlur = size * 0.9;
        context.shadowColor = colorWithAlpha(255, 208, 104, alpha * 0.58);
        context.fillStyle = colorWithAlpha(255, 238, 184, alpha * 0.7);
        context.beginPath();
        traceRoundedRect(context, -size / 2, -size / 2, size, size, size * 0.18);
        context.fill();

        context.shadowBlur = 0;
        context.strokeStyle = colorWithAlpha(255, 255, 255, alpha * 0.25);
        context.lineWidth = 1.2;
        context.beginPath();
        traceRoundedRect(context, -size / 2, -size / 2, size, size, size * 0.18);
        context.stroke();
        context.restore();
    }

    private drawTeamCurrents(
        context: CanvasRenderingContext2D,
        width: number,
        height: number,
        time: number,
        energy: number
    ) {
        const anchors = [
            { x: 0.18, y: 0.24, color: [111, 214, 255] as const },
            { x: 0.76, y: 0.22, color: [113, 255, 198] as const },
            { x: 0.52, y: 0.72, color: [255, 224, 122] as const }
        ];

        anchors.forEach((anchor, index) => {
            const driftX = Math.sin(time * (0.00044 + index * 0.0001) + index * 1.8) * width * 0.022;
            const driftY = Math.cos(time * (0.0005 + index * 0.00012) + index * 2.4) * height * 0.026;
            const centerX = width * anchor.x + driftX;
            const centerY = height * anchor.y + driftY;
            const spread = Math.min(width, height) * mix(0.18, 0.26, unitFromIndex(index, 33));

            this.drawTileCluster(context, centerX, centerY, spread, anchor.color, time, energy, 80 + index * 23, 7);
        });

        const bracketY = height * (this.state.presentationOpen ? 0.18 : 0.24);
        const bracketPoints = [
            { x: width * 0.08, y: bracketY },
            { x: width * 0.26, y: bracketY },
            { x: width * 0.26, y: bracketY + height * 0.035 },
            { x: width * 0.58, y: bracketY + height * 0.035 },
            { x: width * 0.58, y: bracketY },
            { x: width * 0.92, y: bracketY }
        ];

        this.drawAngularGuidePath(context, bracketPoints, [173, 255, 248], 0.04, 1.5, energy);
    }

    private drawGeometricMotes(
        context: CanvasRenderingContext2D,
        width: number,
        height: number,
        time: number,
        energy: number
    ) {
        const moteCount = this.state.presentationOpen ? 12 : 8;

        for (let index = 0; index < moteCount; index += 1) {
            const xPhase = unitFromIndex(index, 52) * Math.PI * 2;
            const yPhase = unitFromIndex(index, 71) * Math.PI * 2;
            const x = mix(
                width * 0.08,
                width * 0.92,
                0.5 + Math.sin(time * (0.0002 + unitFromIndex(index, 61) * 0.0002) + xPhase) * 0.5
            );
            const y = mix(
                height * 0.1,
                height * 0.9,
                0.5 + Math.cos(time * (0.00026 + unitFromIndex(index, 83) * 0.00022) + yPhase) * 0.5
            );
            const size = mix(5.5, 12.5, unitFromIndex(index, 91));
            const pulse = easeInOut(0.5 + Math.sin(time * (0.0014 + unitFromIndex(index, 101) * 0.0005) + xPhase) * 0.5);
            const color = index % 3 === 1 ? ([255, 236, 170] as const) : ([215, 255, 248] as const);
            const rotation = Math.PI * 0.25 + Math.sin(time * 0.0004 + index) * 0.18;

            this.drawSoftTile(context, x, y, size, rotation, color, 0.12 * pulse, energy, 0.16);
        }
    }

    private drawImpactBursts(context: CanvasRenderingContext2D, time: number, energy: number) {
        this.impactBursts.forEach((burst, index) => {
            const progress = clamp((time - burst.createdAt) / burst.duration, 0, 1);
            const bloom = easeInOut(progress);
            const fade = 1 - easeInOut(progress);
            const spread = mix(16, 58, bloom);
            const coreRadius = mix(14, 34, bloom);
            const ringWidth = mix(18, 52, bloom);
            const droplets = [
                { x: -0.34, y: -0.18, size: 9 },
                { x: 0.3, y: -0.24, size: 8 },
                { x: -0.22, y: 0.24, size: 8 },
                { x: 0.38, y: 0.22, size: 9 },
                { x: 0.02, y: -0.36, size: 7 }
            ];

            context.save();
            context.beginPath();
            traceRoundedRect(
                context,
                burst.clipRect.x,
                burst.clipRect.y,
                burst.clipRect.width,
                burst.clipRect.height,
                burst.clipRect.radius
            );
            context.clip();
            context.translate(burst.x, burst.y);
            context.rotate((burst.tiltDeg * Math.PI) / 180);

            context.globalAlpha = 0.42 * fade * energy;
            context.shadowBlur = 24 + bloom * 18;
            context.shadowColor = burst.glow;
            context.fillStyle = burst.soft;
            context.beginPath();
            context.ellipse(0, 0, ringWidth, ringWidth * 0.7, 0, 0, Math.PI * 2);
            context.fill();

            context.globalAlpha = 0.56 * fade * energy;
            context.fillStyle = burst.paint;
            context.beginPath();
            context.arc(0, 0, coreRadius, 0, Math.PI * 2);
            context.fill();

            context.globalAlpha = 0.5 * fade * energy;
            droplets.forEach((droplet, dropletIndex) => {
                context.beginPath();
                context.arc(
                    droplet.x * spread,
                    droplet.y * spread + Math.sin(index + dropletIndex) * 2,
                    droplet.size + bloom * 2,
                    0,
                    Math.PI * 2
                );
                context.fill();
            });

            context.shadowBlur = 0;
            context.globalAlpha = 0.44 * fade * energy;
            context.fillStyle = burst.solid;
            context.beginPath();
            context.arc(0, 0, mix(6, 12, bloom), 0, Math.PI * 2);
            context.fill();

            context.globalAlpha = 0.34 * fade * energy;
            context.strokeStyle = burst.glow;
            context.lineWidth = mix(5, 1.8, bloom);
            context.beginPath();
            context.ellipse(0, 0, ringWidth * 0.74, ringWidth * 0.48, 0, 0, Math.PI * 2);
            context.stroke();
            context.restore();
        });
    }

    private drawEdgeShade(context: CanvasRenderingContext2D, width: number, height: number) {
        const edge = context.createLinearGradient(0, 0, 0, height);
        edge.addColorStop(0, colorWithAlpha(3, 12, 20, 0.12));
        edge.addColorStop(0.16, colorWithAlpha(3, 12, 20, 0));
        edge.addColorStop(0.84, colorWithAlpha(3, 12, 20, 0));
        edge.addColorStop(1, colorWithAlpha(3, 12, 20, 0.16));
        context.fillStyle = edge;
        context.fillRect(0, 0, width, height);

        const vignette = context.createRadialGradient(
            width * 0.5,
            height * 0.5,
            Math.min(width, height) * 0.28,
            width * 0.5,
            height * 0.5,
            Math.max(width, height) * 0.74
        );
        vignette.addColorStop(0, colorWithAlpha(3, 12, 20, 0));
        vignette.addColorStop(1, colorWithAlpha(3, 12, 20, 0.22));
        context.fillStyle = vignette;
        context.fillRect(0, 0, width, height);
    }
}
